'use client'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import debounce from 'lodash/debounce'

import type { NavSearchResults } from '@/types/nav-types'

// Autocomplete fans out to 3 search queries per request (products, articles,
// authors), so keep the keystroke→request rate down: a longer debounce, skip
// 1-char queries, and cancel the in-flight request when a newer one starts.
const SEARCH_DEBOUNCE_MS = 350
const MIN_QUERY_LENGTH = 2
// Keep the spinner up a beat after results land so the render has happened.
const SEARCHING_LINGER_MS = 300

/**
 * The live nav search: a debounced, abortable query against /api/search-nav.
 * Shared by the desktop search drawer (NavSearch) and the mobile sheet's
 * search screen, which render the results differently.
 */
export const useNavSearchQuery = (locale: string) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [results, setResults] = useState<NavSearchResults | null>(null)
  const [searching, setSearching] = useState(false)
  // The term the latest keystroke set: a response for an older term that lands
  // after a newer request must not overwrite the newer results.
  const termRef = useRef('')
  // Cancels the in-flight request when a newer one starts (or on unmount).
  const abortRef = useRef<AbortController | null>(null)
  const lingerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  termRef.current = searchTerm

  const debouncedSearch = useMemo(
    () =>
      debounce(async (search: string) => {
        const trimmed = search.trim()
        // Skip empty / single-char queries — they're noise and the heaviest to
        // serve, and they pad out the rate-limited burst.
        if (trimmed.length < MIN_QUERY_LENGTH) {
          setResults(null)
          return
        }

        abortRef.current?.abort()
        const controller = new AbortController()
        abortRef.current = controller

        if (lingerRef.current) {
          clearTimeout(lingerRef.current)
          lingerRef.current = null
        }
        setSearching(true)
        try {
          const response = await fetch(`/api/search-nav/all?q=${encodeURIComponent(trimmed)}&locale=${locale}`, {
            signal: controller.signal,
          })
          // Transient failure (e.g. upstream 429) — keep the previous results
          // visible rather than blanking the list; the next keystroke retries.
          if (!response.ok) {
            return
          }
          const data = (await response.json()) as NavSearchResults
          if (termRef.current === search) {
            setResults(data)
          }
        } catch (err) {
          // Aborted because a newer search superseded this one — not an error.
          if ((err as Error)?.name === 'AbortError') {
            return
          }
        } finally {
          lingerRef.current = setTimeout(() => setSearching(false), SEARCHING_LINGER_MS)
        }
      }, SEARCH_DEBOUNCE_MS),
    [locale]
  )

  useEffect(() => {
    if (!searchTerm) {
      setResults(null)
      return undefined
    }
    debouncedSearch(searchTerm)
    return () => {
      debouncedSearch.cancel()
      // Drop the in-flight request for the now-stale term (and on unmount).
      abortRef.current?.abort()
    }
  }, [searchTerm, debouncedSearch])

  const reset = useCallback(() => {
    setSearchTerm('')
    setResults(null)
  }, [])

  return { searchTerm, setSearchTerm, results, searching, reset }
}
